import client from '@/apis/client';
import type { Response } from '@/types/client.types';
import type {
  CompanyList,
  GetCompanyInfoRequestProps,
  UserListResponse,
  UserStats,
  GetUsersRequest,
  UserDetail,
  UpdateSubscriptionRequest,
  ToggleAdminRequest,
  ToggleAdminResponse,
  ProxyLoginRequest,
  ProxyLoginResponse,
  UpdateWorkerPriceResponse,
  MonthlyBill,
  MonthlyBillStats,
  MonthlyBillDetail,
  ConfirmPaymentRequest,
  BlockUserRequest,
} from '@/types/admin.types';

export const getCompanyList = ({
  page,
  limit,
  order,
  keyword,
  isAdmin,
}: GetCompanyInfoRequestProps) =>
  client
    .get<Response<CompanyList>>('/admin/getUserList', {
      params: {
        page,
        limit,
        order,
        keyword,
        isAdmin,
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching company list:', error);
      throw error;
    });

export const getUserList = (params: GetUsersRequest) =>
  client
    .get<Response<UserListResponse>>('/admin/getUserList', {
      params,
    })
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching user list:', error);
      throw error;
    });

export const getUserStats = () =>
  client
    .get<Response<UserStats>>('/admin/getUserStats')
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching user stats:', error);
      throw error;
    });

export const getUserDetail = (user_id: number) =>
  client
    .get<Response<UserDetail>>(`/admin/getUserDetail/${user_id}`)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching user detail:', error);
      throw error;
    });

export const updateSubscription = (data: UpdateSubscriptionRequest) =>
  client
    .post<Response<void>>('/admin/updateSubscription', data)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error updating subscription:', error);
      throw error;
    });

export const toggleAdmin = (data: ToggleAdminRequest) =>
  client
    .put<Response<ToggleAdminResponse>>('/admin/user/toggle-admin', data)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error toggling admin status:', error);
      throw error;
    });

export const proxyLogin = (data: ProxyLoginRequest) =>
  client
    .post<Response<ProxyLoginResponse>>('/admin/proxy-login', data)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error proxy login:', error);
      throw error;
    });

export const updateWorkerPrice = (user_id: number, data: { price_per_worker: number }) =>
  client
    .put<Response<UpdateWorkerPriceResponse>>(`/admin/user/${user_id}/worker-price`, data)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error updating worker price:', error);
      throw error;
    });

export const getMonthlyBills = (params: {
  page?: number;
  limit?: number;
  status?: string | string[];
  payment_method?: string;
  start_date?: string;
  end_date?: string;
  billing_month?: string;
  user_id?: number;
  company_id?: number;
  search?: string;
  min_amount?: number;
  max_amount?: number;
  overdue_only?: boolean;
  pending_confirmation_only?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) =>
  client
    .get<Response<{
      bills: MonthlyBill[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>>('/admin/monthly-bills', { params })
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching monthly bills:', error);
      throw error;
    });

export const getMonthlyBillsStats = () =>
  client
    .get<Response<MonthlyBillStats>>('/admin/monthly-bills/stats')
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching monthly bills stats:', error);
      throw error;
    });

export const getMonthlyBillDetail = (billId: number) =>
  client
    .get<Response<MonthlyBillDetail>>(`/admin/monthly-bill/${billId}`)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error fetching monthly bill detail:', error);
      throw error;
    });

export const confirmMonthlyBillPayment = (billId: number, data: ConfirmPaymentRequest) =>
  client
    .put<Response<void>>(`/admin/monthly-bill/${billId}/confirm-payment`, data)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error confirming monthly bill payment:', error);
      throw error;
    });

export const blockUser = (userId: number, data: BlockUserRequest) =>
  client
    .put<Response<void>>(`/admin/user/${userId}/block`, data)
    .then((res) => res.data)
    .catch((error) => {
      console.error('Error blocking user:', error);
      throw error;
    });